import React from 'react';
import HeadText from 'components/shared/text/HeadText';
import BulletList from 'components/shared/text/BulletList';
import Expandable from 'components/shared/Expandable';
import PlanType from 'components/forms/pro/PlanType';
import PaymentForm from 'components/forms/pro/PaymentForm';
import LoginForm from 'components/forms/LoginForm';
import SignUpForm from 'components/forms/SignUpForm';
import CheckCircleIcon from 'images/pro/greenCheck-filled.svg';
import { ServerCommunicationUtil } from 'utils/server_communication_util';
import { ProPageStrings } from 'react_components/mixins/shared/string_constants_mixin';
import { initializeOptimizely } from 'utils/OptimizelyHelpers';
import CustomIntlProvider from 'components/CustomIntlProvider';
import { WebProPurchaseLocation } from '@alltrails/analytics/amplitudeEnums';
import { logProPurchaseGetStartedClicked, logProPurchasePageViewed } from '@alltrails/analytics/amplitudeFunctions';
import RecurlyPlan from 'types/RecurlyPlan';
import PlanCode from 'types/PlanCode';
import PlanTypeType from 'types/PlanTypeType';
import { FormattedMessage } from 'react-intl';
import * as baseStyles from '../../styles/baseStyles.module.scss';
import * as styles from './styles/styles.module.scss';
import PaymentMethod from 'types/PaymentMethod';
import CurrencyCode from 'types/CurrencyCode';

const PRO_PAGE_TEXT = { ...ProPageStrings.paymentText, creditCardForm: ProPageStrings.creditCardForm };

const OPTIMIZELY_PRICING_FEATURE = 'pro_monthly_pricing';
const OPTIMIZELY_PRICE_VARIABLE = 'pro_monthly_price_dollars';
const OPTIMIZELY_RECURLY_VARIABLE = 'recurly_plan_code';

const UNITED_STATES_COUNTRY_ID = 313;

function getFeaturesContent() {
  const featuresList = <BulletList bullet={<img src={CheckCircleIcon} style={{ color: '#428a13' }} />} list={ProPageStrings.featuresList} />;

  return (
    <div className={`${styles.panel} ${styles.left}`}>
      <HeadText text={ProPageStrings.headText} />

      <div className={styles.mobile}>
        <Expandable title={ProPageStrings.featuresCollapseHead}>{featuresList}</Expandable>
      </div>

      <div className={styles.desktop}>{featuresList}</div>
    </div>
  );
}

type ProPageProps = {
  isAuthed?: boolean;
  facebookAppId: number;
  planTypes?: PlanTypeType[];
  paymentTypes?: PaymentMethod[];
  optimizelySettings: {
    datafile: string;
  };
  optimizelyAttributeData: {
    country?: string;
    authenticated?: boolean;
    mobileBrowser?: boolean;
  };
  amazonSellerId: string;
  currencyCode: CurrencyCode;
  amazonClientId: string;
  enableRecaptcha: boolean;
  initialPlan?: PlanCode;
  coupon?: {
    code: string;
  };
  forceMonthlyPricePoint: boolean;
  countryId: number;
  locale: string;
  recurlyPublicKey: string;
  annualPlan: PlanTypeType;
};

type ProPageState = {
  isAuthed: boolean;
  isRegistrationShown: boolean;
  isTypeSelected: boolean;
  optimizelyUserId: string;
  optimizelyInstance: any;
  couponCode: string;
  activePaymentTab: any;
  activePlanType: PlanCode;
  isCouponInputExpanded?: boolean;
  submitting: boolean;
  serverError: string;
  shownErrors: any;
  planTypes: PlanTypeType[];
  couponError?: string;
  verifiedCoupon: {
    code: string;
  };
  isCouponApplied: boolean;
  recurlyPublicKey?: string;
  user?: any;
  isFeaturesCollapsed: boolean;
};

class ProPage extends React.Component<ProPageProps, ProPageState> {
  constructor(props: ProPageProps) {
    super(props);

    // ?
    const activePlanType = this.props.initialPlan ? this.props.initialPlan : props.planTypes[0].planCode;

    this.state = {
      submitting: false,
      serverError: null,
      shownErrors: {},
      isAuthed: this.props.isAuthed,
      isFeaturesCollapsed: true,
      isRegistrationShown: true,
      isTypeSelected: this.props.initialPlan != null,
      activePlanType,
      activePaymentTab: 0,
      verifiedCoupon: this.props.coupon,
      couponCode: this.props.coupon ? this.props.coupon.code : null,
      isCouponApplied: false,
      planTypes: [...this.props.planTypes],
      optimizelyInstance: null,
      optimizelyUserId: null
    };
  }

  componentDidMount() {
    logProPurchasePageViewed();

    // Set this to true to test optimizely locally with incorrect country id
    const forcePricingExperiment = false;
    const isUSA = this.props.countryId === UNITED_STATES_COUNTRY_ID;

    this.setUpOptimizely();

    if (this.props.forceMonthlyPricePoint) {
      this.forceMonthlyLowPricePoint();
    } else if (forcePricingExperiment || isUSA) {
      this.setUpPricingExperiment();
    }

    if (this.state.verifiedCoupon) {
      this.setState({ isCouponApplied: true, isCouponInputExpanded: true, submitting: false });
    }
  }

  forceMonthlyLowPricePoint() {
    const planTypes = [...this.state.planTypes];
    const monthlyPrice = 5.99;

    planTypes.forEach(planType => {
      const percentage = Math.floor((1 - Number.parseFloat(planType.priceMonth) / monthlyPrice) * 100);
      const amount = `Save ${percentage}%`;
      planType.discountOverride = true;
      planType.discountOverrideAmount = amount;
    });

    const experimentalPlan = {
      allowCoupon: false,
      numberOfMonths: '1',
      planCode: PlanCode.Monthly,
      priceMonth: String(monthlyPrice),
      priceTotal: String(monthlyPrice),
      showDiscount: false,
      recurlyPlanCode: RecurlyPlan.Monthly
    };

    // We want the experimental monthly plan at the top, so unshift instead of push
    planTypes.unshift(experimentalPlan);

    this.setState({ planTypes });
  }

  setUpOptimizely() {
    const { locale, optimizelySettings } = this.props;

    try {
      const [optimizelyInstance, optimizelyUserId] = initializeOptimizely(optimizelySettings);
      const optimizelyAttributes = { locale };
      optimizelyInstance.setUser({ id: optimizelyUserId, attributes: optimizelyAttributes });
      this.setState({ optimizelyInstance, optimizelyUserId }, this.setUpPricingExperiment);
    } catch (error) {
      console.error('Error setting up Optimizely', error);
    }
  }

  setUpPricingExperiment() {
    const { optimizelyInstance, optimizelyUserId } = this.state;

    if (!(optimizelyInstance && optimizelyUserId)) {
      return;
    }

    const experimentEnabled = optimizelyInstance.isFeatureEnabled(OPTIMIZELY_PRICING_FEATURE, optimizelyUserId);

    if (experimentEnabled) {
      const monthlyPrice = optimizelyInstance.getFeatureVariableDouble(OPTIMIZELY_PRICING_FEATURE, OPTIMIZELY_PRICE_VARIABLE, optimizelyUserId);
      const recurlyPlanCode = optimizelyInstance.getFeatureVariableString(
        OPTIMIZELY_PRICING_FEATURE,
        OPTIMIZELY_RECURLY_VARIABLE,
        optimizelyUserId
      ) as RecurlyPlan;

      // User is in control group. Do not offer monthly plan.
      if (recurlyPlanCode === RecurlyPlan.OptimizelyControl || monthlyPrice === 0) {
        return;
      }

      const planTypes = [...this.state.planTypes];

      planTypes.forEach(planType => {
        const percentage = Math.floor((1 - Number.parseFloat(planType.priceMonth) / monthlyPrice) * 100);
        const amount = `Save ${percentage}%`;
        planType.discountOverride = true;
        planType.discountOverrideAmount = amount;
      });

      const experimentalPlan = {
        allowCoupon: false,
        numberOfMonths: '1',
        planCode: PlanCode.Monthly,
        priceMonth: monthlyPrice,
        priceTotal: monthlyPrice,
        showDiscount: false,
        recurlyPlanCode
      };

      // We want the experimental monthly plan at the top, so unshift instead of push
      planTypes.unshift(experimentalPlan);

      this.setState({ planTypes });
    }
  }

  clearErrors() {
    // Single function to clear shownErrors, server errors and coupon errors
    this.setState({
      shownErrors: {},
      couponError: '',
      serverError: null
    });
  }

  handleTypeClick = (activePlanType: PlanCode) => {
    this.setState({ activePlanType });
  };

  userFormSuccessCallback = (data: { user: any }) => {
    this.clearErrors();
    this.setState({ user: data.user, isAuthed: true, submitting: false });
  };

  handleRegistrationSuccess = (data: { user: any }) => {
    this.setState({ user: data.user, isAuthed: true });
  };

  handleRegisterClick = () => {
    this.clearErrors();
    this.setState({ isRegistrationShown: true });
  };

  handleLoginClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    this.clearErrors();
    this.setState({ isRegistrationShown: false });
  };

  handleCouponCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    this.setState({ couponCode: value, couponError: '' });
  };

  handleRemoveCoupon = () => {
    this.setState({
      isCouponInputExpanded: false,
      couponCode: null,
      isCouponApplied: false,
      verifiedCoupon: null
    });
  };

  clearSelectedType = () => {
    ServerCommunicationUtil.handleGaqPush(['_trackEvent', 'pro_step_2', 'change_plan']);
    this.setState({ isTypeSelected: false });
  };

  handleCouponVerification = (couponCode: string) => {
    // coupon_code is only allowed param in the API
    if (this.state.submitting) {
      // Not ideal, but this will prevent double submission (until loading state is added)
      return;
    }
    this.setState({ submitting: true });
    const data = {
      coupon_code: couponCode
    };
    ServerCommunicationUtil.postApiEndpoint(
      '/api/alltrails/coupons/verify',
      data,
      (data: { code: any }[]) => {
        this.setState({ verifiedCoupon: data[0], isCouponApplied: true, submitting: false });
        ServerCommunicationUtil.handleGaqPush(['_trackEvent', 'coupon_code', data[0].code]);
        // growth-868: force user from free trial to annual plan if they have a valid coupon
        this.setState({ activePlanType: PlanCode.Annual, planTypes: [this.props.annualPlan] });
      },
      () => {
        this.setState({ isCouponApplied: false, couponError: ProPageStrings.creditCardForm.couponCode.validation.generic, submitting: false });
      },
      () => {}
    );
  };

  getPlanTypesForm() {
    const handleTypeSubmit = () => {
      this.clearErrors();

      logProPurchaseGetStartedClicked();

      this.setState({ isTypeSelected: true });
    };

    return (
      <PlanType
        activePlanType={this.state.activePlanType}
        buttonText={<FormattedMessage defaultMessage="Get Started" />}
        currencyCode={this.props.currencyCode}
        handleClick={this.handleTypeClick}
        handleSubmit={handleTypeSubmit}
        isCouponApplied={this.state.isCouponApplied}
        planTypes={this.state.planTypes}
        headerText={<FormattedMessage defaultMessage="Pick your plan" />}
        verifiedCoupon={this.state.verifiedCoupon}
      />
    );
  }

  getLoginForm() {
    return (
      <LoginForm
        locale={this.props.locale}
        handleLoginSuccess={this.userFormSuccessCallback}
        handleRegisterClick={this.handleRegisterClick}
        enableRecaptcha={this.props.enableRecaptcha}
        facebookAppId={this.props.facebookAppId}
      />
    );
  }

  getRegistrationForm() {
    return (
      <SignUpForm
        isProPage
        countryId={this.props.countryId}
        handleRegistrationSuccess={this.handleRegistrationSuccess}
        handleLoginClick={this.handleLoginClick}
        enableRecaptcha={this.props.enableRecaptcha}
        facebookAppId={this.props.facebookAppId}
        redirectTo={null}
      />
    );
  }

  getPaymentForm() {
    const submitText =
      this.state.activePlanType === PlanCode.SevenDayTrial ? (
        <FormattedMessage defaultMessage="Try Pro free + subscribe" />
      ) : (
        <FormattedMessage defaultMessage="Get AllTrails Pro" />
      );

    return (
      <PaymentForm
        submitText={submitText}
        text={PRO_PAGE_TEXT}
        locale={this.props.locale}
        recurlyPublicKey={this.props.recurlyPublicKey}
        activePlanType={this.state.activePlanType}
        forceMonthlyPricePoint={this.props.forceMonthlyPricePoint}
        planTypes={this.state.planTypes}
        handleApplyCoupon={() => this.handleCouponVerification(this.state.couponCode)}
        handleCouponCodeChange={this.handleCouponCodeChange}
        toggleCouponCode={() => this.setState({ isCouponInputExpanded: !this.state.isCouponInputExpanded })}
        handleRemoveCoupon={this.handleRemoveCoupon}
        isCouponApplied={this.state.isCouponApplied}
        verifiedCoupon={this.state.verifiedCoupon}
        couponError={this.state.couponError} // Coupon Codes use a different error messaging system since they verify when applying
        isCouponInputExpanded={this.state.isCouponInputExpanded}
        isCouponInputShown
        updateActivePlan={this.handleTypeClick}
        activePaymentTab={this.state.activePaymentTab}
        paymentTypes={this.props.paymentTypes}
        enableRecaptcha={this.props.enableRecaptcha}
        amazonClientId={this.props.amazonClientId}
        amazonSellerId={this.props.amazonSellerId}
        couponCode={this.state.couponCode}
        currencyCode={this.props.currencyCode}
        optimizelyInstance={this.state.optimizelyInstance}
        optimizelyUserId={this.state.optimizelyUserId}
        optimizelyAttributeData={this.props.optimizelyAttributeData}
        webProPurchaseLocation={WebProPurchaseLocation.ProPage}
      />
    );
  }

  render() {
    let formBody;

    // todo switch to react router
    if (this.state.isTypeSelected) {
      if (this.state.isAuthed) {
        formBody = this.getPaymentForm();
      } else if (this.state.isRegistrationShown) {
        formBody = this.getRegistrationForm();
      } else {
        formBody = this.getLoginForm();
      }
    } else {
      formBody = this.getPlanTypesForm();
    }

    return (
      <CustomIntlProvider>
        <div>
          <div className={baseStyles.container}>
            <div className={baseStyles.row}>
              <div className={`${styles.body} ${styles.topoBackground}`}>
                {!this.state.isTypeSelected && getFeaturesContent()}
                <div className={`${styles.panel} ${!this.state.isTypeSelected ? styles.right : ''}`}>{formBody}</div>
              </div>
            </div>
          </div>
        </div>
      </CustomIntlProvider>
    );
  }
}

export default ProPage;
